import { z } from "zod";
import { backdropSchema } from "./backdrop";
import { themeLogoSchema } from "./logo";

/**
 * How a deck looks: the stage's background, its accent, its typeface and a logo — the
 * brand a presenter is running the session for. Chosen in the editor (from a preset or
 * one of the organisation's saved brands) and frozen into the session with the rest of
 * the deck (Rule 6), so a brand edited next month cannot restyle last month's results.
 *
 * Everything else a screen needs — text on that background, card surfaces, muted text —
 * is DERIVED (themeColours), so no combination of choices can put white text on a
 * white stage.
 */

export const THEME_FONTS = ["bricolage", "inter", "poppins", "playfair", "nunito"] as const;
export type ThemeFont = (typeof THEME_FONTS)[number];

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, "must be a #rrggbb colour");

export const themeSchema = z
  .object({
    background: hex,
    accent: hex,
    font: z.enum(THEME_FONTS),
    /** Sized and backed by each screen from its own measurements (contracts/logo.ts). */
    logo: themeLogoSchema,
    /** A picture behind the whole stage, shaded with the background colour (contracts/backdrop.ts). */
    backgroundImage: backdropSchema.nullable().default(null),
  })
  .strict();

export type Theme = z.infer<typeof themeSchema>;

export const DEFAULT_THEME: Theme = { background: "#17132e", accent: "#5b3df5", font: "bricolage", logo: null, backgroundImage: null };

/** Ready-made looks, for a presenter with no brand of their own. Names are in the catalog (`theme.preset.<id>`). */
export const THEME_PRESETS: ReadonlyArray<{ id: string; theme: Theme }> = [
  { id: "engage", theme: DEFAULT_THEME },
  { id: "midnight", theme: { background: "#0b1220", accent: "#38bdf8", font: "inter", logo: null, backgroundImage: null } },
  { id: "sunset", theme: { background: "#2a0f1f", accent: "#f97316", font: "poppins", logo: null, backgroundImage: null } },
  { id: "forest", theme: { background: "#0d2018", accent: "#22c55e", font: "nunito", logo: null, backgroundImage: null } },
  { id: "ocean", theme: { background: "#062a3a", accent: "#06b6d4", font: "inter", logo: null, backgroundImage: null } },
  { id: "paper", theme: { background: "#f7f3ea", accent: "#c2410c", font: "playfair", logo: null, backgroundImage: null } },
];

/**
 * Whatever a deck or snapshot holds, as a usable theme: field by field, falling back to
 * the default. Decks from before themes existed hold `{}`.
 */
export function readTheme(value: unknown): Theme {
  const v = (value ?? {}) as Record<string, unknown>;
  const pick = <K extends keyof Theme>(key: K): Theme[K] => {
    const parsed = themeSchema.shape[key].safeParse(v[key]);
    return parsed.success ? (parsed.data as Theme[K]) : DEFAULT_THEME[key];
  };
  return {
    background: pick("background"),
    accent: pick("accent"),
    font: pick("font"),
    logo: pick("logo"),
    backgroundImage: pick("backgroundImage"),
  };
}

function rgb(hexColour: string): [number, number, number] {
  const n = parseInt(hexColour.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hexColour: string): number {
  const [r, g, b] = rgb(hexColour).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

/** WCAG contrast ratio, 1 to 21. */
export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m);
  return (x! + 0.05) / (y! + 0.05);
}

function mix(a: string, b: string, amountOfB: number): string {
  const [ar, ag, ab] = rgb(a);
  const [br, bg, bb] = rgb(b);
  const m = (x: number, y: number) => Math.round(x + (y - x) * amountOfB);
  return `#${[m(ar, br), m(ag, bg), m(ab, bb)].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/** The stage's full palette, from a theme's two colours. */
export function themeColours(theme: Theme): {
  background: string;
  surface: string;
  foreground: string;
  muted: string;
  accent: string;
  /** Text on the accent colour. */
  onAccent: string;
  light: boolean;
} {
  const light = luminance(theme.background) > 0.45;
  const foreground = light ? "#17142a" : "#ffffff";
  return {
    background: theme.background,
    surface: mix(theme.background, foreground, light ? 0.07 : 0.1),
    foreground,
    muted: mix(foreground, theme.background, light ? 0.38 : 0.3),
    accent: theme.accent,
    // Whichever reads better on it — a fixed cut-off put white on sky blue.
    onAccent: contrast("#17142a", theme.accent) > contrast("#ffffff", theme.accent) ? "#17142a" : "#ffffff",
    light,
  };
}

/** Whether two looks are the same — to mark the brand or preset a deck is wearing. */
export function sameTheme(a: Theme | null, b: Theme | null): boolean {
  return a !== null && b !== null && JSON.stringify(readTheme(a)) === JSON.stringify(readTheme(b));
}

/**
 * A saved brand: a named theme the organisation reuses across decks. The default one
 * is what every new deck starts in.
 */
export const brandSchema = z
  .object({
    brandId: z.string().uuid(),
    name: z.string(),
    theme: themeSchema,
    isDefault: z.boolean(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type Brand = z.infer<typeof brandSchema>;

export const brandInputSchema = z.object({ name: z.string().trim().min(1).max(60), theme: themeSchema }).strict();
export type BrandInput = z.infer<typeof brandInputSchema>;

/** Choosing the brand new decks start in; null for none (the standard look). */
export const defaultBrandSchema = z.object({ brandId: z.string().uuid().nullable() }).strict();
export type DefaultBrand = z.infer<typeof defaultBrandSchema>;
